export interface ExportColumn<T> {
  label: string;
  getValue: (row: T) => unknown;
}

function printable(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' && /^\d{4}-\d\d-\d\dT/.test(value))
    return new Date(value).toLocaleString();
  if (typeof value === 'string') return value.replaceAll('_', ' ');
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint')
    return value.toString();
  if (typeof value === 'object') return JSON.stringify(value);
  return '';
}

function download(contents: BlobPart, type: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function filename(title: string, extension: string): string {
  const date = new Date().toISOString().slice(0, 10);
  return `${title.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-')}-${date}.${extension}`;
}

export function downloadCsv<T>(title: string, columns: ExportColumn<T>[], rows: T[]): void {
  const quote = (value: string) => `"${value.replaceAll('"', '""')}"`;
  const lines = [
    columns.map((column) => quote(column.label)).join(','),
    ...rows.map((row) => columns.map((column) => quote(printable(column.getValue(row)))).join(',')),
  ];
  download(`\ufeff${lines.join('\r\n')}`, 'text/csv;charset=utf-8', filename(title, 'csv'));
}

function pdfText(value: string): string {
  return value
    .normalize('NFKD')
    .replaceAll(/[^\x20-\x7e]/g, '?')
    .replaceAll('\\', '\\\\')
    .replaceAll('(', '\\(')
    .replaceAll(')', '\\)');
}

function wrap(value: string, width: number): string[] {
  if (!value) return [''];
  const words = value.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const chunks = word.match(new RegExp(`.{1,${width}}`, 'g')) ?? [''];
    for (const chunk of chunks) {
      if (line && line.length + chunk.length + 1 > width) {
        lines.push(line);
        line = '';
      }
      line = line ? `${line} ${chunk}` : chunk;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function pdfTextCommand(
  text: string,
  x: number,
  y: number,
  size: number,
  font: 'F1' | 'F2' = 'F1',
  color = '0.12 0.18 0.16 rg',
): string {
  return `BT /${font} ${size} Tf ${color} ${x.toFixed(2)} ${y.toFixed(2)} Td (${pdfText(text)}) Tj ET`;
}

function fitLines(value: string, width: number, maximum = 3): string[] {
  const characters = Math.max(5, Math.floor((width - 8) / 4.2));
  const lines = wrap(value || '-', characters);
  if (lines.length <= maximum) return lines;
  const visible = lines.slice(0, maximum);
  const last = visible[maximum - 1] ?? '';
  visible[maximum - 1] = `${last.slice(0, Math.max(1, characters - 3))}...`;
  return visible;
}

function columnWidths<T>(columns: ExportColumn<T>[], rows: T[], available: number): number[] {
  const minimum = columns.length >= 8 ? 54 : 68;
  const weights = columns.map((column) => {
    const longest = rows.reduce(
      (length, row) => Math.max(length, printable(column.getValue(row)).length),
      column.label.length,
    );
    return Math.sqrt(Math.min(36, Math.max(8, longest)));
  });
  const distributable = Math.max(0, available - minimum * columns.length);
  const totalWeight = weights.reduce((total, weight) => total + weight, 0);
  const widths = weights.map((weight) => minimum + (distributable * weight) / totalWeight);
  const difference = available - widths.reduce((total, width) => total + width, 0);
  widths[widths.length - 1] = (widths.at(-1) ?? 0) + difference;
  return widths;
}

export function createPdfDocument<T>(title: string, columns: ExportColumn<T>[], rows: T[]): string {
  const pageWidth = 842;
  const pageHeight = 595;
  const margin = 36;
  const tableWidth = pageWidth - margin * 2;
  const widths = columnWidths(columns, rows, tableWidth);
  const values = rows.map((row) => columns.map((column) => printable(column.getValue(row)) || '-'));
  const pages: string[][] = [];
  let commands: string[] = [];
  let rowTop = 0;

  const addTableHeader = () => {
    const headerHeight = 26;
    commands.push(
      `0.08 0.20 0.16 rg ${margin} ${rowTop - headerHeight} ${tableWidth} ${headerHeight} re f`,
    );
    let x = margin;
    columns.forEach((column, index) => {
      const width = widths[index]!;
      commands.push(
        `0.55 0.68 0.63 RG 0.5 w ${x} ${rowTop - headerHeight} ${width} ${headerHeight} re S`,
      );
      commands.push(
        pdfTextCommand(
          fitLines(column.label, width, 1)[0] ?? column.label,
          x + 5,
          rowTop - 17,
          7.5,
          'F2',
          '1 1 1 rg',
        ),
      );
      x += width;
    });
    rowTop -= headerHeight;
  };

  const beginPage = (continued: boolean) => {
    commands = [];
    commands.push(
      pdfTextCommand(`${title} Report`, margin, pageHeight - 48, 19, 'F2', '0.08 0.34 0.25 rg'),
      pdfTextCommand(
        continued
          ? 'CDRMetrics License Manager - Detailed Records (Continued)'
          : 'CDRMetrics License Manager - Filtered Data Export',
        margin,
        pageHeight - 67,
        10,
      ),
      pdfTextCommand(
        `Generated: ${new Date().toLocaleString()}   |   Records: ${rows.length}`,
        margin,
        pageHeight - 84,
        8.5,
        'F1',
        '0.36 0.43 0.40 rg',
      ),
      `0.72 0.79 0.76 RG 0.8 w ${margin} ${pageHeight - 96} m ${pageWidth - margin} ${pageHeight - 96} l S`,
      pdfTextCommand('Detailed Records', margin, pageHeight - 119, 11, 'F2'),
    );
    rowTop = pageHeight - 132;
    addTableHeader();
  };

  beginPage(false);
  values.forEach((row, rowIndex) => {
    const cellLines = row.map((cell, index) => fitLines(cell, widths[index]!));
    const lineCount = Math.max(...cellLines.map((lines) => lines.length));
    const rowHeight = Math.max(24, lineCount * 10 + 8);
    if (rowTop - rowHeight < 44) {
      pages.push(commands);
      beginPage(true);
    }
    if (rowIndex % 2 === 1)
      commands.push(
        `0.96 0.98 0.97 rg ${margin} ${rowTop - rowHeight} ${tableWidth} ${rowHeight} re f`,
      );
    let x = margin;
    cellLines.forEach((lines, columnIndex) => {
      const width = widths[columnIndex]!;
      commands.push(
        `0.76 0.82 0.79 RG 0.45 w ${x} ${rowTop - rowHeight} ${width} ${rowHeight} re S`,
      );
      lines.forEach((line, lineIndex) => {
        commands.push(pdfTextCommand(line, x + 4, rowTop - 13 - lineIndex * 10, 7));
      });
      x += width;
    });
    rowTop -= rowHeight;
  });
  if (values.length === 0) {
    commands.push(
      `0.76 0.82 0.79 RG 0.45 w ${margin} ${rowTop - 30} ${tableWidth} 30 re S`,
      pdfTextCommand('No Records Match the Current Filters.', margin + 6, rowTop - 19, 8),
    );
  }
  pages.push(commands);
  pages.forEach((page, index) => {
    page.push(
      pdfTextCommand(
        `${title} Report  |  Page ${index + 1} of ${pages.length}`,
        margin,
        22,
        7.5,
        'F1',
        '0.40 0.47 0.44 rg',
      ),
    );
  });

  const fontId = 3 + pages.length * 2;
  const boldFontId = fontId + 1;
  const objects: string[] = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    `<< /Type /Pages /Kids [${pages.map((_, index) => `${3 + index * 2} 0 R`).join(' ')}] /Count ${pages.length} >>`,
  ];
  pages.forEach((page, index) => {
    const pageId = 3 + index * 2;
    const contentId = pageId + 1;
    const stream = page.join('\n');
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 ${fontId} 0 R /F2 ${boldFontId} 0 R >> >> /Contents ${contentId} 0 R >>`,
      `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
    );
  });
  objects.push(
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>',
  );

  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  pdf += offsets
    .slice(1)
    .map((offset) => `${String(offset).padStart(10, '0')} 00000 n \n`)
    .join('');
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return pdf;
}

export function downloadPdf<T>(title: string, columns: ExportColumn<T>[], rows: T[]): void {
  const pdf = createPdfDocument(title, columns, rows);
  download(pdf, 'application/pdf', filename(title, 'pdf'));
}
